import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { SessionUser } from "../../shared/auth/access";
import { productionGateway } from "../production-execution/production-gateway";
import type { ProductionOperationalNotification } from "../production-execution/models";
import { WeeklyPlanningModule } from "../weekly-planning/WeeklyPlanningModule";
import { ProductionPlanImport } from "./ProductionPlanImport";

type ProductionRequirementsModuleProps = {
  notify: (message: string) => void;
  dataRefreshVersion: number;
  user: SessionUser;
};

export function ProductionRequirementsModule(
  props: ProductionRequirementsModuleProps,
) {
  const [view, setView] = useState<"NATIVE" | "EXCEL">("NATIVE");
  const [nativeDirty, setNativeDirty] = useState(false);
  const [nativeWeekStart, setNativeWeekStart] = useState<string | null>(null);
  const selectView = (next: "NATIVE" | "EXCEL") => {
    if (next === view) return;
    if (nativeDirty && !window.confirm("Kaydedilmemiş haftalık plan değişiklikleri var. Plan kayıtlarına geçmek istiyor musunuz?")) return;
    setView(next);
  };
  return <>
    <header className="module-header production-requirements-header">
      <div>
        <h1>Üretim planlama</h1>
      </div>
    </header>

    <nav className="production-requirements-tabs" aria-label="Üretim planlama çalışma alanı">
      <button type="button" className={view === "NATIVE" ? "is-active" : ""} aria-current={view === "NATIVE" ? "page" : undefined} onClick={() => selectView("NATIVE")}>Haftalık plan</button>
      <button type="button" className={view === "EXCEL" ? "is-active" : ""} aria-current={view === "EXCEL" ? "page" : undefined} onClick={() => selectView("EXCEL")}>Plan kayıtları ve Excel</button>
    </nav>
    {view === "NATIVE" ? <WeeklyPlanningModule
      user={props.user}
      dataRefreshVersion={props.dataRefreshVersion}
      onDirtyChange={setNativeDirty}
      initialWeekStart={nativeWeekStart}
      onWeekStartChange={setNativeWeekStart}
    /> : <ProductionPlanImport notify={props.notify} onDraftImported={(weekStart) => {
      setNativeWeekStart(weekStart);
      setView("NATIVE");
    }} />}
    <PlanningDeviationPanel {...props} />
  </>;
}

function PlanningDeviationPanel({
  notify,
  dataRefreshVersion,
}: ProductionRequirementsModuleProps) {
  const [items, setItems] = useState<ProductionOperationalNotification[]>([]);
  const load = useCallback(async () => {
    try {
      const next = await productionGateway.notifications();
      const planningEvents = next.filter((item) =>
        ["SHIFT_CARRYOVER", "SHIFT_CHANGED", "LINE_CHANGED", "RUN_CANCELLED", "PLAN_OVERRUN"].includes(item.eventType),
      );
      setItems(planningEvents);
      const unread = planningEvents.filter((item) => !item.viewedAt);
      if (unread.length) {
        await Promise.all(unread.map((item) => productionGateway.viewNotification(item.id)));
        setItems((current) => current.map((item) => unread.some((candidate) => candidate.id === item.id) ? { ...item, viewedAt: new Date().toISOString() } : item));
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : "Üretim sapmaları yüklenemedi.");
    }
  }, [notify]);
  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(interval);
  }, [dataRefreshVersion, load]);
  if (!items.length) return null;
  const activeItems = items.filter((item) => item.disposition !== "CANCELLED");
  const cancelledItems = items.filter((item) => item.disposition === "CANCELLED");
  const quantity = (value: number | null | undefined, unit?: string) => value == null
    ? "—"
    : `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(value)} ${unit ?? ""}`;
  const expectedEnd = (value: string | null | undefined) => value
    ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value))
    : "Hesaplanamadı";
  return (
    <section className="planning-deviation-panel" aria-label="Üretim planı sapmaları">
      <header>
        <div>
          <span>ÜRETİMDEN GELEN SAPMALAR</span>
          <h2>Plan güncellemesi gereken işler</h2>
          <p>Vardiya amirinin aktardığı veya planı etkileyen işler</p>
        </div>
        <span className="planning-deviation-count"><AlertTriangle size={16} />{activeItems.length} açık etki</span>
      </header>
      <div className="planning-deviation-list">
        {items.slice(0, 8).map((item) => (
          <article key={item.id} className={item.disposition === "CANCELLED" ? "is-cancelled" : ""}>
            <span className="planning-deviation-state">
              {item.disposition === "CANCELLED" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            </span>
            <span className="planning-deviation-order"><small>İŞ EMRİ</small><strong>{item.workOrderNo ?? "—"}</strong><em>{item.productName}</em></span>
            <span><small>HAT</small><strong>{item.lineName ?? "—"}</strong></span>
            <span><small>YENİ PLAN</small><strong>{item.shiftDate ?? "—"}</strong><em>{item.shiftCode}</em></span>
            <span className="planning-deviation-quantity"><small>PLAN / GERÇEKLEŞEN</small><strong>{quantity(item.plannedQuantity, item.unit)} / {quantity(item.producedQuantity, item.unit)}</strong><em>{quantity(item.remainingQuantity, item.unit)} kaldı</em></span>
            <span><small>TAHMİNİ BİTİŞ</small><strong>{expectedEnd(item.expectedEndAt)}</strong><em>{item.plannedEndAt && item.expectedEndAt && new Date(item.expectedEndAt) > new Date(item.plannedEndAt) ? "Plan sonrasına sarkıyor" : "Plan içinde"}</em></span>
            <span className="planning-deviation-message"><small>ETKİ</small><strong>{item.message}</strong></span>
            <ArrowRight size={17} />
          </article>
        ))}
      </div>
      {cancelledItems.length ? <footer>{cancelledItems.length} iptal edilen iş kayıt amacıyla listede tutuluyor.</footer> : null}
    </section>
  );
}
